import * as React from 'react';
import Chip from '@mui/material/Chip';
import Stack from '@mui/material/Stack';
import PhotoLibraryIcon from '@mui/icons-material/PhotoLibrary';
import { Panel, Row, Col, Alert } from 'react-bootstrap';
import { isBrokerAgent } from '../../../utils/brokerAgentUtils';
import { IOrderRow, ImageType, IImageThumbnail } from '../Entities';
import { Translate } from '../../../../../shared/LanguageService';
import ProductPropertiesIcons from '../../../../propertyIcons/ProductPropertiesIcons';
import SelectMultipleToolbox from '../SelectMultipleToolbox';
import EditedImagesDropzone from '../EditedImagesDropzone';
import * as Features from '../../../../../shared/Features';
import * as PhotoStatus from '../../../../../shared/PhotoStatus';
import ImageProcessingList from './ImageProcessingList';
import { CustomerApprovalIndicator } from './CustomerApprovalIndicator';
import { UserRole } from '../../../../../shared/types';
import * as ProductProperty from '../../../../../shared/ProductProperties';

interface IProps {
    item: IOrderRow;
    additionalItems: IAdditionalOrderRow;
    showCustomerApproval: boolean;
    editedPhotosUploadInProgress: boolean;
    showModalForImage: boolean;
    numberOfPhotosInRows: any[];
    orderId: number;
    allowStitching: boolean;
    user: any;
    photographerIsEditor: boolean;
    photoStatus: any;
    categories: number[];
    allowChangeCategories: boolean;
    deletePhoto: any;
    highImageSizeSettings: any;
    normalImageSizeSettings: any;
    onNormalImageUploadDone: any;
    onUploadDone: any;
    downloadFile: (url: string, data?: any) => Promise<any>;
    ctrlIsDown: boolean;
    showCompareImages: boolean;
    setShowCompareImages: (value: boolean) => void;
    selectedImages: number[];
    setSelectedImages: (images: number[], tabKey: number) => void;
    setIsWriting: (value: boolean) => void;
}

interface IState {
    selectingMultiple: boolean;
    selectedCategory: number;
    displayStitchRadio: boolean;
    leftStitchImage: any;
    rightStitchImage: any;
    showReturnImageCommentSection: boolean;
    selectMultipleActionLoading: boolean;
    showCreateStitchedImageModal: boolean;
}

interface IAdditionalOrderRow {
    orderRowId: number;
    expectedQuantity: number;
    isUploadable: boolean;
    productName: string;
    productProperties: any[];
}

const productsToExclude = [ProductProperty.BlueSky, ProductProperty.InteriorBlueSky];

class EditedImagesRow extends React.Component<IProps, IState> {
    constructor(props: IProps) {
        super(props);

        this.state = {
            selectingMultiple: false,
            selectedCategory: 1,
            displayStitchRadio: false,
            leftStitchImage: null,
            rightStitchImage: null,
            showReturnImageCommentSection: false,
            selectMultipleActionLoading: false,
            showCreateStitchedImageModal: false
        };

        this.canDeleteFile = this.canDeleteFile.bind(this);
    }

    shouldShowDropzone = () => {
        return (
            (Features.hasFeature(Features.AddEditedImages, this.props.user) &&
                (this.props.photoStatus.currentStatus === PhotoStatus.UploaderApproved ||
                    this.props.photoStatus.currentStatus === PhotoStatus.EditorUploading ||
                    this.props.photoStatus.currentStatus === PhotoStatus.Se360Disapproved)) ||
            (this.props.photoStatus.photographerIsEditor &&
                Features.hasFeature(
                    Features.HandleEditedImagesWhenPhotographerIsEditor,
                    this.props.user
                ) &&
                (this.props.photoStatus.currentStatus === PhotoStatus.Created ||
                    this.props.photoStatus.currentStatus === PhotoStatus.Uploading ||
                    this.props.photoStatus.currentStatus === PhotoStatus.UploaderApproved ||
                    this.props.photoStatus.currentStatus === PhotoStatus.EditorUploading ||
                    this.props.photoStatus.currentStatus === PhotoStatus.Se360Disapproved)) ||
            Features.hasFeature(Features.CanUploadImagesAnyTime, this.props.user)
        );
    };

    displayNormalSizeDropzone(orderRow: IOrderRow) {
        return (
            orderRow.thumbnails.length > 0 &&
            (Features.hasFeature(Features.UploadNormalImage, this.props.user) ||
                (this.props.photoStatus.photographerIsEditor &&
                    Features.hasFeature(
                        Features.HandleEditedImagesWhenPhotographerIsEditor,
                        this.props.user
                    ))) &&
            this.props.normalImageSizeSettings &&
            this.props.normalImageSizeSettings.autoScale === false
        );
    }

    toggleRadio = () => {
        if (this.state.displayStitchRadio === true) {
            this.setState({
                leftStitchImage: null,
                rightStitchImage: null
            } as IState);
        }

        this.setState(
            (prevState) =>
                ({
                    displayStitchRadio: !prevState.displayStitchRadio
                } as IState)
        );
    };

    toggleSelectingMultiple = () => {
        if (this.state.selectingMultiple) {
            this.props.setSelectedImages([], 1);
        }

        this.setState(
            (prevState) =>
                ({
                    selectingMultiple: !prevState.selectingMultiple
                } as IState)
        );
    };

    selectAllImages = (thumbnails: IImageThumbnail[]) => {
        let { user } = this.props;
        let images = isBrokerAgent(user.details.role)
            ? thumbnails.filter((img) => img.isCustomerApproved)
            : thumbnails;
        let imageIds = images.map((img) => img.id);
        if (imageIds.length === this.props.selectedImages.length) {
            this.props.setSelectedImages([], 1);
        } else {
            this.props.setSelectedImages([...imageIds], 1);
        }
    };

    imageSelected = (imageId: number, selected: boolean) => {
        if (selected) {
            this.props.setSelectedImages([...this.props.selectedImages, imageId], 1);
        } else {
            this.props.setSelectedImages(
                [...this.props.selectedImages.filter((id: number) => id !== imageId)],
                1
            );
        }
    };

    toggleReturnImageCommentSection = () => {
        if (!this.state.showReturnImageCommentSection) {
            this.props.setIsWriting(true);
        } else {
            this.props.setIsWriting(false);
        }
        this.setState(
            (prevState) =>
                ({
                    showReturnImageCommentSection: !prevState.showReturnImageCommentSection
                } as IState)
        );
    };

    setSelectMultipleActionLoading = (selectMultipleActionLoading: boolean) => {
        if (selectMultipleActionLoading === true) {
            this.setState({ selectMultipleActionLoading: true } as IState);
        } else {
            this.setState({
                selectMultipleActionLoading: false,
                selectingMultiple: false
            } as IState);
            this.props.setSelectedImages([], 1);
        }
    };

    leftStitchSelected = (image: IImageThumbnail) => {
        this.setState({ leftStitchImage: image } as IState);
    };

    rightStitchSelected = (image: IImageThumbnail) => {
        this.setState({ rightStitchImage: image } as IState);
    };

    setShowCreateStitchedImageModal = (showCreateStitchedImageModal: boolean) => {
        this.setState({ showCreateStitchedImageModal } as IState);
    };

    canDeleteFile(): boolean {
        return (
            (this.props.photoStatus.currentStatus >= PhotoStatus.EditorApproved
                ? Features.hasFeature(Features.DeleteImagesAcceptedOrder, this.props.user)
                : true) &&
            (Features.hasFeature(Features.DeleteEditedImage, this.props.user) ||
                (this.props.photographerIsEditor &&
                    Features.hasFeature(
                        Features.HandleEditedImagesWhenPhotographerIsEditor,
                        this.props.user
                    )))
        );
    }

    calculateNumberOfImagesInProgress(item) {
        let numberOfImagesInProgress: number[] = [];
        let detailsForOrder =
            this.props.numberOfPhotosInRows.length > 0 &&
            this.props.numberOfPhotosInRows.filter((x) => x.orderRowId === item.id);

        let numberOfPhotosAdded =
            detailsForOrder.length > 0 && detailsForOrder[0].numberOfAddedPhotos;
        let numberOfThumbnails = item.thumbnails.length;

        if (numberOfPhotosAdded >= numberOfThumbnails) {
            let inProgress = numberOfPhotosAdded - numberOfThumbnails;
            numberOfImagesInProgress = Array.from({ length: inProgress }, (value, index) => index);
        }
        return numberOfImagesInProgress;
    }

    render() {
        const { item } = this.props;
        const { additionalItems } = this.props;

        let numberOfImagesInProgress = this.calculateNumberOfImagesInProgress(item);

        return (
            <div className="panel-group" key={item.id}>
                <Panel className="image-processing-panel">
                    <Panel.Heading>
                        <Panel.Title>{this.renderHeader(item, additionalItems)}</Panel.Title>
                    </Panel.Heading>
                    <Panel.Body>
                        {this.props.editedPhotosUploadInProgress && (
                            <p>
                                {Translate('order-page/edited-image/upload-in-progress')}{' '}
                                <span className="load-spin-dark" />
                            </p>
                        )}
                        <ImageProcessingList
                            imageThumbnails={item.thumbnails}
                            showModalForImage={this.props.showModalForImage}
                            imageType={ImageType.EDITED}
                            orderRowName={item.name}
                            orderRowId={item.id}
                            key={item.id}
                            numberOfPhotosInRow={
                                this.props.numberOfPhotosInRows.filter(
                                    (x) => x.orderRowId === item.id
                                )[0]
                            }
                            shouldShowAddedPhotos={this.props.numberOfPhotosInRows.length > 0}
                            orderId={this.props.orderId}
                            productProperties={item.orderRowProductProperties}
                            allowStitching={this.props.allowStitching}
                            onDelete={this.props.deletePhoto}
                            canDelete={this.canDeleteFile}
                            user={this.props.user}
                            batchChanges={Features.hasFeature(
                                Features.ApproveImagesByCustomer,
                                this.props.user
                            )}
                            photoStatus={this.props.photoStatus}
                            categories={this.props.categories}
                            allowChangeCategories={this.props.allowChangeCategories}
                            selectedImages={this.props.selectedImages}
                            selectingMultiple={this.state.selectingMultiple}
                            imageSelected={this.imageSelected}
                            toggleReturnImageCommentSection={this.toggleReturnImageCommentSection}
                            showReturnImageCommentSection={this.state.showReturnImageCommentSection}
                            setShowCompareImages={this.props.setShowCompareImages}
                            showCompareImages={this.props.showCompareImages}
                            ctrlIsDown={this.props.ctrlIsDown}
                            displayStitchRadio={this.state.displayStitchRadio}
                            leftStitchImage={this.state.leftStitchImage}
                            rightStitchImage={this.state.rightStitchImage}
                            leftStitchSelected={this.leftStitchSelected}
                            rightStitchSelected={this.rightStitchSelected}
                            showCreateStitchedImageModal={this.state.showCreateStitchedImageModal}
                            setShowCreateStitchedImageModal={this.setShowCreateStitchedImageModal}
                            toggleRadio={this.toggleRadio}
                            selectMultipleActionLoading={this.state.selectMultipleActionLoading}
                            setSelectMultipleActionLoading={this.setSelectMultipleActionLoading}
                            editedTab={true}
                            imagesInProgress={numberOfImagesInProgress}
                        />
                    </Panel.Body>
                    <Panel.Footer>{this.renderFooter(item)}</Panel.Footer>
                </Panel>
            </div>
        );
    }

    renderHeader(item: IOrderRow, additionalItems) {
        const canDeletePhotos =
            this.props.photoStatus.currentStatus >= PhotoStatus.EditorApproved
                ? Features.hasFeature(Features.DeleteImagesAcceptedOrder, this.props.user)
                : true;

        let numberOfAdditionalImages =
            additionalItems &&
            additionalItems
                .map((x) => x.expectedQuantity)
                .reduce((sum, expectedQuantity) => sum + expectedQuantity, 0);

        let overrideswithCategoryNormal = item.thumbnails.filter((img: IImageThumbnail) => {
            return img.overrideProperties && img.overrideProperties.categoryId == 1;
        }).length;
        let noOverrideWithCategoryNomal = item.thumbnails.filter((img: IImageThumbnail) => {
            return !img.overrideProperties && img.categoryId == 1;
        }).length;

        let { photoStatus } = this.props;

        const userRole = this.props.user.details.role;

        const selectingMultiple =
            this.state.selectingMultiple ||
            (this.props.selectedImages ? this.props.selectedImages.length > 0 : false);

        return (
            <>
                <div className="order-row-header">
                    <span className="product-name">{item.name}</span>{' '}
                    <span className="number-of-images">
                        (
                        {Translate('unedited-images/number-of-images', {
                            numberOfImages: item.numberOfImages
                        })}
                        )
                    </span>
                    <ProductPropertiesIcons
                        properties={
                            userRole == UserRole.Photographer
                                ? item.orderRowProductProperties.filter((property) => {
                                      return (
                                          property.value !==
                                          productsToExclude.find((x) => x === property.value)
                                      );
                                  })
                                : item.orderRowProductProperties
                        }
                    />
                    {additionalItems &&
                        additionalItems.map((x: any, i: number) => {
                            return (
                                <span key={i}>
                                    <span> + </span>
                                    <span className="product-name">{x.productName}</span>
                                    <span className="number-of-images">
                                        (
                                        {Translate('unedited-images/number-of-images', {
                                            numberOfImages: x.expectedQuantity
                                        })}
                                        )
                                    </span>
                                </span>
                            );
                        })}
                    <Stack direction="row" spacing={1} alignItems="center">
                        {!this.props.photoStatus.hasEditCustomerImages &&
                            !this.props.photoStatus.hasFlex &&
                            this.props.numberOfPhotosInRows.length > 0 &&
                            this.props.numberOfPhotosInRows.filter(
                                (x) => x.orderRowId === item.id
                            )[0] && (
                                <Chip
                                    size="small"
                                    variant="outlined"
                                    icon={<PhotoLibraryIcon />}
                                    label={
                                        noOverrideWithCategoryNomal +
                                        overrideswithCategoryNormal +
                                        '/' +
                                        (this.props.numberOfPhotosInRows.filter(
                                            (x) => x.orderRowId === item.id
                                        )[0].numberOfWantedPhotos +
                                            (numberOfAdditionalImages
                                                ? numberOfAdditionalImages
                                                : 0) +
                                            ' ' +
                                            Translate(
                                                'image-processing/page/order-row/header/ordered-images'
                                            ))
                                    }
                                />
                            )}
                        {this.props.showCustomerApproval && (
                            <CustomerApprovalIndicator photoStatus={photoStatus} />
                        )}
                    </Stack>
                </div>
                <SelectMultipleToolbox
                    allowChangeCategories={
                        this.props.allowChangeCategories &&
                        !Features.hasFeature(Features.ApproveImagesByCustomer, this.props.user)
                    }
                    allowStitching={this.props.allowStitching}
                    canDelete={() =>
                        Features.hasFeature(Features.DeleteEditedImage, this.props.user) ||
                        (this.props.photographerIsEditor &&
                            Features.hasFeature(
                                Features.HandleEditedImagesWhenPhotographerIsEditor,
                                this.props.user
                            ))
                    }
                    canDeletePhotos={canDeletePhotos}
                    categories={this.props.categories}
                    displayStitchRadio={this.state.displayStitchRadio}
                    imageThumbnails={item.thumbnails}
                    leftStitchImage={this.state.leftStitchImage}
                    orderId={this.props.orderId}
                    rightStitchImage={this.state.rightStitchImage}
                    selectAllImages={this.selectAllImages}
                    selectMultipleActionLoading={this.state.selectMultipleActionLoading}
                    selectedCategory={this.state.selectedCategory}
                    selectedImages={this.props.selectedImages}
                    selectingMultiple={selectingMultiple}
                    setSelectMultipleActionLoading={this.setSelectMultipleActionLoading}
                    setShowCompareImages={this.props.setShowCompareImages}
                    setShowCreateStitchedImageModal={this.setShowCreateStitchedImageModal}
                    shouldShowAddedPhotos={this.props.item.fileNames.length > 0}
                    showCreateStitchedImageModal={this.state.showCreateStitchedImageModal}
                    toggleRadio={this.toggleRadio}
                    toggleReturnImageCommentSection={this.toggleReturnImageCommentSection}
                    toggleSelectingMultiple={this.toggleSelectingMultiple}
                    downloadFile={this.props.downloadFile}
                />
            </>
        );
    }

    renderFooter(item: IOrderRow) {
        return (
            this.shouldShowDropzone() && (
                <Row className="footer-row">
                    <Col md={this.displayNormalSizeDropzone(item) ? 6 : 12}>
                        {this.props.highImageSizeSettings && (
                            <Alert bsSize="large" bsStyle="info">
                                <h4>
                                    {Translate(
                                        'image-processing/edited/upload/image-size/settings/high/title'
                                    )}
                                </h4>
                                {Translate(
                                    'image-processing/edited/upload/image-size/settings/size',
                                    {
                                        value: this.props.highImageSizeSettings
                                            ? this.props.highImageSizeSettings.size
                                            : 'N/A'
                                    }
                                )}
                                <br />
                                {Translate(
                                    'image-processing/edited/upload/image-size/settings/dpi',
                                    {
                                        value: this.props.highImageSizeSettings
                                            ? this.props.highImageSizeSettings.dpi
                                            : 'N/A'
                                    }
                                )}
                            </Alert>
                        )}
                        <EditedImagesDropzone
                            orderId={this.props.orderId}
                            orderRowId={item.id}
                            orderRowFileNames={item.fileNames}
                            onUploadDone={this.props.onUploadDone}
                            doNotWarnAboutNotExistingFilenames={
                                this.props.photoStatus.photographerIsEditor
                            }
                        />
                    </Col>
                    {this.displayNormalSizeDropzone(item) && (
                        <Col md={6}>
                            <Alert bsSize="large" bsStyle="warning">
                                <h4>
                                    {Translate(
                                        'image-processing/edited/upload/image-size/settings/normal/title'
                                    )}
                                </h4>
                                {Translate(
                                    'image-processing/edited/upload/image-size/settings/size',
                                    {
                                        value: this.props.normalImageSizeSettings
                                            ? this.props.normalImageSizeSettings.size
                                            : 'N/A'
                                    }
                                )}
                                <br />
                                {Translate(
                                    'image-processing/edited/upload/image-size/settings/dpi',
                                    {
                                        value: this.props.normalImageSizeSettings
                                            ? this.props.normalImageSizeSettings.dpi
                                            : 'N/A'
                                    }
                                )}
                            </Alert>
                            <EditedImagesDropzone
                                orderId={this.props.orderId}
                                orderRowId={item.id}
                                orderRowFileNames={item.fileNames}
                                onUploadDone={this.props.onNormalImageUploadDone}
                                normalImagesDropzone={true}
                                doNotWarnAboutNotExistingFilenames={true}
                            />
                        </Col>
                    )}
                </Row>
            )
        );
    }
}

export default EditedImagesRow;
